import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Map as MapIcon, ExternalLink, User, MapPin, Clock,
  RefreshCw, AlertCircle,
} from 'lucide-react';
import { onsiteApiFetch } from '../config/api.js';
import { cn } from '../lib/utils.js';
import type { OnsiteJob, OnsiteTechnician } from '../../../api/src/types/onsite-types.js';

interface MapTechLocation {
  tech_id: string;
  tech_name: string;
  status: 'available' | 'on_route' | 'on_site';
  latitude?: number;
  longitude?: number;
  last_checkin_at?: string;
  color?: string;
  current_job_id?: string;
}

interface MapJobLocation {
  id: string;
  title: string;
  customer_name: string;
  customer_address: string;
  latitude?: number;
  longitude?: number;
  status: string;
  assigned_tech_id?: string;
  assigned_tech_name?: string;
  scheduled_start: string;
}

interface MapResponse {
  technicians: MapTechLocation[];
  jobs: MapJobLocation[];
}

const TECH_STATUS_DOT: Record<string, { color: string; label: string }> = {
  available: { color: 'bg-green-500', label: 'Available' },
  on_route:  { color: 'bg-orange-500', label: 'On Route' },
  on_site:   { color: 'bg-red-500', label: 'On Site' },
};

function formatRelativeTime(iso?: string): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function MapPage() {
  const qc = useQueryClient();
  const [assigningJobId, setAssigningJobId] = useState<string | null>(null);

  const { data, isLoading, error, dataUpdatedAt, refetch } = useQuery({
    queryKey: ['dispatch', 'map'],
    queryFn: () => onsiteApiFetch<MapResponse>('/api/v1/onsite/dispatch/map'),
    refetchInterval: 30_000,
  });

  const { data: techsData } = useQuery({
    queryKey: ['technicians'],
    queryFn: () => onsiteApiFetch<{ technicians: OnsiteTechnician[] }>('/api/v1/onsite/technicians'),
  });

  const assign = useMutation({
    mutationFn: (payload: { job_id: string; tech_id: string }) =>
      onsiteApiFetch('/api/v1/onsite/dispatch/assign', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dispatch', 'map'] });
      setAssigningJobId(null);
    },
  });

  const techs = data?.technicians ?? [];
  const jobs = data?.jobs ?? [];
  const unassignedJobs = jobs.filter(j => !j.assigned_tech_id);
  const allTechs = techsData?.technicians ?? [];

  return (
    <div className="flex h-full flex-col lg:flex-row overflow-hidden">
      {/* Map canvas */}
      <div className="flex-1 flex flex-col min-h-[300px]">
        <div className="flex items-center justify-between gap-2 border-b border-border bg-card px-4 py-3">
          <div>
            <h1 className="flex items-center gap-2 text-lg font-semibold text-foreground">
              <MapIcon className="h-5 w-5 text-primary" />
              Live Map
            </h1>
            <p className="text-xs text-muted-foreground">
              Updated {formatRelativeTime(new Date(dataUpdatedAt).toISOString())} · refreshes every 30s
            </p>
          </div>
          <button
            onClick={() => refetch()}
            className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-secondary hover:text-foreground"
            aria-label="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-auto bg-background p-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center gap-3 py-20 text-muted-foreground">
              <AlertCircle className="h-10 w-10 text-red-400" />
              <p className="font-medium text-foreground">Unable to load map data</p>
              <p className="text-sm">{(error as Error).message}</p>
            </div>
          ) : techs.length === 0 && jobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-20 text-muted-foreground">
              <MapIcon className="h-12 w-12" />
              <p className="text-lg font-medium">No live locations</p>
              <p className="text-sm text-center max-w-xs">
                Technicians will appear here once they start their day and check in to a job.
              </p>
            </div>
          ) : (
            <SchematicMap techs={techs} jobs={jobs} />
          )}
        </div>
      </div>

      {/* Sidebar */}
      <aside className="w-full lg:w-96 shrink-0 border-t lg:border-t-0 lg:border-l border-border bg-card flex flex-col">
        <div className="border-b border-border p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
            Technicians ({techs.length})
          </h2>
          <div className="space-y-2">
            {techs.length === 0 && (
              <p className="text-xs text-muted-foreground">No tech locations.</p>
            )}
            {techs.map(tech => {
              const statusCfg = TECH_STATUS_DOT[tech.status] ?? TECH_STATUS_DOT.available;
              return (
                <div key={tech.tech_id} className="flex items-center gap-3 rounded-lg border border-border bg-secondary p-2">
                  <div className="relative">
                    <div
                      className="flex h-10 w-10 items-center justify-center rounded-full"
                      style={{ backgroundColor: (tech.color ?? '#f97316') + '33' }}
                    >
                      <User className="h-5 w-5" style={{ color: tech.color ?? '#f97316' }} />
                    </div>
                    <span className={cn('absolute -right-0.5 -bottom-0.5 h-3 w-3 rounded-full border-2 border-card', statusCfg.color)} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{tech.tech_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {statusCfg.label} · {formatRelativeTime(tech.last_checkin_at)}
                    </p>
                  </div>
                  {tech.latitude && tech.longitude && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${tech.latitude},${tech.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded p-1.5 text-muted-foreground hover:bg-background hover:text-primary"
                      aria-label="Open in Google Maps"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
            Jobs ({jobs.length})
            {unassignedJobs.length > 0 && (
              <span className="ml-2 rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] text-amber-400">
                {unassignedJobs.length} unassigned
              </span>
            )}
          </h2>
          <div className="space-y-2">
            {jobs.length === 0 && (
              <p className="text-xs text-muted-foreground">No jobs yet.</p>
            )}
            {jobs.map(job => (
              <JobRow
                key={job.id}
                job={job}
                techs={allTechs}
                assigning={assigningJobId === job.id}
                onStartAssign={() => setAssigningJobId(job.id)}
                onCancelAssign={() => setAssigningJobId(null)}
                onAssign={techId => assign.mutate({ job_id: job.id, tech_id: techId })}
                submitting={assign.isPending}
              />
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
}

function JobRow({ job, techs, assigning, onStartAssign, onCancelAssign, onAssign, submitting }: {
  job: MapJobLocation;
  techs: OnsiteTechnician[];
  assigning: boolean;
  onStartAssign: () => void;
  onCancelAssign: () => void;
  onAssign: (techId: string) => void;
  submitting: boolean;
}) {
  const [selected, setSelected] = useState<string>('');

  return (
    <div className="rounded-lg border border-border bg-secondary p-2">
      <div className="flex items-start justify-between gap-2">
        <Link to={`/jobs/${job.id}`} className="min-w-0 flex-1 hover:text-primary">
          <p className="text-sm font-medium text-foreground truncate">{job.title}</p>
          <p className="text-xs text-muted-foreground truncate">{job.customer_name}</p>
          {job.customer_address && (
            <p className="flex items-center gap-1 text-xs text-muted-foreground/80 truncate">
              <MapPin className="h-3 w-3 shrink-0" />
              {job.customer_address}
            </p>
          )}
        </Link>
        {job.latitude && job.longitude && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${job.latitude},${job.longitude}`}
            target="_blank"
            rel="noreferrer"
            className="rounded p-1.5 text-muted-foreground hover:bg-background hover:text-primary"
            aria-label="Open in Google Maps"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>

      <div className="mt-1.5 flex items-center gap-2 text-xs">
        {job.assigned_tech_name ? (
          <span className="flex items-center gap-1 text-muted-foreground">
            <User className="h-3 w-3" />
            {job.assigned_tech_name}
          </span>
        ) : (
          assigning ? (
            <div className="flex flex-1 items-center gap-1.5">
              <select
                value={selected}
                onChange={e => setSelected(e.target.value)}
                className="flex-1 rounded border border-border bg-background px-2 py-1 text-xs text-foreground focus:border-primary focus:outline-none"
              >
                <option value="">Pick tech...</option>
                {techs.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <button
                onClick={() => selected && onAssign(selected)}
                disabled={!selected || submitting}
                className="rounded bg-primary px-2 py-1 text-xs text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {submitting ? '...' : 'OK'}
              </button>
              <button
                onClick={onCancelAssign}
                className="rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-background"
              >
                ✕
              </button>
            </div>
          ) : (
            <button
              onClick={onStartAssign}
              className="rounded bg-primary/20 px-2 py-0.5 text-xs font-medium text-primary hover:bg-primary/30"
            >
              Assign
            </button>
          )
        )}
        <span className="ml-auto flex items-center gap-1 text-muted-foreground/80">
          <Clock className="h-3 w-3" />
          {new Date(job.scheduled_start).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
}

// Schematic SVG map — renders tech avatars in a grid with status dots
function SchematicMap({ techs, jobs }: { techs: MapTechLocation[]; jobs: MapJobLocation[] }) {
  // Compute simple bounding box if we have coords; otherwise lay out in a grid
  const haveCoords = [...techs, ...jobs].some(e => e.latitude != null && e.longitude != null);

  if (!haveCoords) {
    return (
      <div className="rounded-xl border border-border bg-card p-6">
        <p className="mb-4 text-sm text-muted-foreground">
          Schematic view — no GPS coordinates available. Showing team layout.
        </p>
        <div className="grid grid-cols-3 gap-4 sm:grid-cols-4 md:grid-cols-6">
          {techs.map(t => {
            const cfg = TECH_STATUS_DOT[t.status] ?? TECH_STATUS_DOT.available;
            return (
              <div key={t.tech_id} className="flex flex-col items-center gap-2 rounded-lg border border-border bg-secondary p-3 text-center">
                <div className="relative">
                  <div
                    className="flex h-12 w-12 items-center justify-center rounded-full"
                    style={{ backgroundColor: (t.color ?? '#f97316') + '33' }}
                  >
                    <User className="h-6 w-6" style={{ color: t.color ?? '#f97316' }} />
                  </div>
                  <span className={cn('absolute right-0 bottom-0 h-3.5 w-3.5 rounded-full border-2 border-card', cfg.color)} />
                </div>
                <p className="text-xs font-medium text-foreground truncate w-full">{t.tech_name}</p>
                <p className="text-[10px] text-muted-foreground">{cfg.label}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Compute bounds
  const allPoints = [
    ...techs.filter(t => t.latitude != null && t.longitude != null).map(t => ({ lat: t.latitude!, lng: t.longitude!, kind: 'tech' as const, ref: t })),
    ...jobs.filter(j => j.latitude != null && j.longitude != null).map(j => ({ lat: j.latitude!, lng: j.longitude!, kind: 'job' as const, ref: j })),
  ];
  const lats = allPoints.map(p => p.lat);
  const lngs = allPoints.map(p => p.lng);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const padding = 20;
  const W = 800, H = 480;
  function project(lat: number, lng: number) {
    const x = padding + ((lng - minLng) / Math.max(0.0001, maxLng - minLng)) * (W - padding * 2);
    const y = padding + (1 - (lat - minLat) / Math.max(0.0001, maxLat - minLat)) * (H - padding * 2);
    return { x, y };
  }

  return (
    <div className="rounded-xl border border-border bg-card p-2 overflow-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto min-w-[600px]">
        {/* Grid */}
        <defs>
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeOpacity="0.08" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width={W} height={H} fill="url(#grid)" className="text-muted-foreground" />

        {/* Job pins */}
        {jobs.filter(j => j.latitude != null && j.longitude != null).map(job => {
          const { x, y } = project(job.latitude!, job.longitude!);
          return (
            <g key={`job-${job.id}`}>
              <circle cx={x} cy={y} r={6} className="fill-blue-500 stroke-card" strokeWidth={2} />
              <text x={x + 10} y={y + 4} className="fill-foreground text-[11px] font-medium">
                {job.customer_name}
              </text>
            </g>
          );
        })}

        {/* Tech markers */}
        {techs.filter(t => t.latitude != null && t.longitude != null).map(tech => {
          const { x, y } = project(tech.latitude!, tech.longitude!);
          const cfg = TECH_STATUS_DOT[tech.status] ?? TECH_STATUS_DOT.available;
          const fill = cfg.color.replace('bg-', '').replace('-500', '');
          const color = fill === 'green' ? '#22c55e' : fill === 'orange' ? '#f97316' : '#ef4444';
          return (
            <g key={`tech-${tech.tech_id}`}>
              <circle cx={x} cy={y} r={12} fill={tech.color ?? '#f97316'} fillOpacity={0.25} />
              <circle cx={x} cy={y} r={8} fill={color} stroke="white" strokeWidth={2} />
              <text x={x} y={y - 14} textAnchor="middle" className="fill-foreground text-[11px] font-semibold">
                {tech.tech_name.split(' ')[0]}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="mt-2 flex flex-wrap gap-4 px-2 pb-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-green-500" />Available</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-orange-500" />On Route</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-red-500" />On Site</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-blue-500" />Job Site</span>
      </div>
    </div>
  );
}
